import AppBar from '@material-ui/core/AppBar'
import Button from '@material-ui/core/Button'
import Tab from '@material-ui/core/Tab'
import Tabs from '@material-ui/core/Tabs'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import AddIcon from '@material-ui/icons/Add'
import React from 'react'
import { Redirect, useHistory, useLocation } from 'react-router-dom'

import { buildFeatureSet, canAccessTab, getCredentials } from '../../cache/auth'
import PageHeader from '../../components/PageHeader'
import { TabPanel } from '../../helpers/TabPanel'
import { TicketTab } from '../../helpers/ticketWorkflow'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import supportRoleManagement from '../../staticRoleManagement/supportRoleManagement'
import { ROLE } from '../../staticRoleManagement/types'
import SupportAgentQueue from '../SupportAgentQueue'
import SupportSlaPolicies from '../SupportSlaPolicies'
import SupportTickets from '../SupportTickets'
import useStyles from './styles/index.styles'

/**
 * One drawer entry for support, with the sub-pages as tabs (the InventoryTabs
 * shape). Replaces three separate top-level drawer entries.
 *
 * WHY THE TICKET FILTERS ARE TABS TOO. SupportTickets carried its own inner
 * Mine/Affecting-us/Assigned strip. Nesting that under a page-level strip would
 * stack two Material tab bars, which nothing else in this app does. Promoting
 * them to this one strip keeps a single bar and puts the choice in the URL —
 * previously it was component state, so `Assigned` could not be linked to,
 * bookmarked, or restored on reload.
 */

type SupportTabValue =
  | 'mine'
  | 'affecting-us'
  | 'assigned'
  | 'queue'
  | 'sla-policies'

/** The URL segment for each ticket list, and the protocol value it selects. */
const TICKET_TAB_BY_PAGE: Record<string, TicketTab> = {
  mine: 'MINE',
  'affecting-us': 'AFFECTING_US',
  assigned: 'ASSIGNED'
}

const DEFAULT_PAGE: SupportTabValue = 'mine'

interface SupportTabDefinition {
  value: SupportTabValue
  label: string
  /** Admin-only tabs are omitted from the strip entirely, not disabled. */
  adminOnly: boolean
}

const SUPPORT_TABS: SupportTabDefinition[] = [
  { value: 'mine', label: 'Mine', adminOnly: false },
  { value: 'affecting-us', label: 'Affecting us', adminOnly: false },
  { value: 'assigned', label: 'Assigned', adminOnly: false },
  { value: 'queue', label: 'Support queue', adminOnly: true },
  { value: 'sla-policies', label: 'SLA policies', adminOnly: true }
]

export default function SupportTabs() {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const history = useHistory()
  const { pathname } = useLocation()

  const page = pathname.slice(pathname.lastIndexOf('/') + 1)

  const isAdmin = getRoleFromStorage() === ROLE.ADMIN

  // Two filters, because one is not enough and neither substitutes for the
  // other. canAccessTab is the portal's tab mechanism (§4.3) and covers the
  // FEATURE dimension; it reads `requiredFeatures` and nothing else, so it
  // cannot hide an admin tab from a tenant. The role check is what does that.
  const featureSet = buildFeatureSet(getCredentials())
  const routeConfig = supportRoleManagement.find(
    (item) => item.route === '/support/:page'
  )

  // `/support` alone lands on the list every user can read.
  if (page === 'support') {
    return <Redirect to={`${pathname}/${DEFAULT_PAGE}`} />
  }

  const visibleTabs = SUPPORT_TABS.filter(
    (tab) =>
      (!tab.adminOnly || isAdmin) &&
      canAccessTab(routeConfig, tab.value, featureSet)
  )

  // An unknown segment, or an admin-only one reached by a non-admin, goes to
  // the default tab rather than rendering an empty strip. The screens behind
  // the admin tabs are also guarded and the server re-checks the admin claim on
  // every call, so this is the affordance, not the boundary.
  //
  // Derived from pathname rather than built from `lang`, so the redirect stays
  // inside whatever locale prefix the user is already on. Interpolating the
  // language would send someone reading /fa/... to /en/... on a bad segment.
  if (!visibleTabs.some((tab) => tab.value === page)) {
    const sectionBase = pathname.slice(0, pathname.lastIndexOf('/'))

    return <Redirect to={`${sectionBase}/${DEFAULT_PAGE}`} />
  }

  const ticketTab = TICKET_TAB_BY_PAGE[page]

  return (
    <div className={classes.root}>
      <PageHeader title={getTranslate('Support')} />
      <AppBar position="static" color="transparent">
        {/* `scrollable` below sm rather than fullWidth: an admin sees five
            tabs, and fullWidth divides the width equally with no overflow. At
            ~360px that is ~70px per tab for labels like "Affecting us" — and
            the Persian translations run longer still, so they would wrap then
            truncate. */}
        <Tabs
          value={page}
          indicatorColor="primary"
          textColor="primary"
          variant={isSmallScreen ? 'scrollable' : 'fullWidth'}
          scrollButtons="auto"
          aria-label={getTranslate('Support')}>
          {visibleTabs.map((tab) => (
            <Tab
              key={tab.value}
              disableRipple
              value={tab.value}
              className={classes.tab}
              label={getTranslate(tab.label)}
              // TabPanel renders aria-labelledby={`full-width-tab-${index}`}
              // and id={`full-width-tabpanel-${index}`}. Without these two the
              // panel's label points at nothing and the tab announces no
              // controlled region — MUI v4 does not generate either.
              id={`full-width-tab-${tab.value}`}
              aria-controls={`full-width-tabpanel-${tab.value}`}
              to={`/support/${tab.value}`}
              component={LocalizedLink}
            />
          ))}
        </Tabs>
      </AppBar>

      {/* Only on the ticket lists: on the queue and the policy editor a "New
          ticket" button would be an unrelated action in the primary slot. */}
      {ticketTab && (
        <div className={classes.actionBar}>
          {/* §2's create action: contained primary, startIcon AddIcon, right,
              collapsing the label on small screens.
              The label stays "New ticket" rather than the convention's literal
              "Add <Thing>" — "Add ticket" is not what a support desk calls
              this, and the string is already translated and in use. Icon,
              weight, placement and collapse follow the convention; only the
              noun does not. */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => history.push(`/${lang}/support/tickets/create`)}>
            {isSmallScreen ? getTranslate('New') : getTranslate('New ticket')}
          </Button>
        </div>
      )}

      {Object.keys(TICKET_TAB_BY_PAGE).map((ticketPage) => (
        <TabPanel
          key={ticketPage}
          value={page}
          index={ticketPage}
          dir={theme.direction}
          className={classes.tabPanel}>
          <SupportTickets tab={TICKET_TAB_BY_PAGE[ticketPage]} />
        </TabPanel>
      ))}

      {isAdmin && (
        <>
          <TabPanel
            value={page}
            index="queue"
            dir={theme.direction}
            className={classes.tabPanel}>
            <SupportAgentQueue />
          </TabPanel>
          <TabPanel
            value={page}
            index="sla-policies"
            dir={theme.direction}
            className={classes.tabPanel}>
            <SupportSlaPolicies />
          </TabPanel>
        </>
      )}
    </div>
  )
}
